/**
 * RunMode.jsx — Open House Planner run mode (/openhouse?tab=run)
 *
 * Day-of field execution, mobile-first. Walks the crew stop-by-stop through
 * the optimized plan: big current-stop card with native nav + agent contact,
 * a collapsible pitch cheat sheet, arrive/log-outcome/skip actions, and an
 * auto re-route (POST /openhouse/route from the current position) when a stop
 * is finished or skipped so the rest of the day re-optimizes on the fly.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Button, Chip, Paper, Alert,
  Select, MenuItem, FormControl, InputLabel, Divider,
  Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Switch, FormControlLabel, ToggleButton, ToggleButtonGroup, CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Navigation as NavigationIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  SkipNext as SkipNextIcon,
  Route as RouteIcon,
  Flag as FlagIcon,
  PlaceOutlined as PlaceIcon,
} from '@mui/icons-material';
import {
  useOhPlans, useUpdateOhPlan, useOhPlanStops, useUpdateOhPlanStop,
  useOpenHouses, useOptimizeRoute,
} from '../../hooks/useOpenHouses';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../layout/Toast';
import { logAction } from '../../lib/actionLogger';

const PT = 'America/Los_Angeles';
const VILLAGE_COLOR = '#E879F9';

// ── Pitch cheat sheet (kept inline — decoupled from the templates lib) ──────
const PITCH = {
  intro: "Hi — I'm not a buyer, I'll be quick. Cyprian, Roto Flight Media here in Santa Barbara — gorgeous staging on this one.",
  offer: "We're producing a short Open House Series this month — cinematic 45–60s reels of standout SB listings with the hosting agent on camera. Free, delivered tomorrow, fully yours to post. Want this one in the series?",
  permission: "It's the seller's home — totally your call. We can stick to exteriors and details if interiors are off-limits, and nothing posts until you approve.",
  fallback: "If we feature other listings in the series, want me to text you the results so you can see what it looks like?",
  redLines: [
    'Yield to buyers instantly — stop talking, step aside or leave. Never speak to buyers.',
    '20-minute cap per house.',
    'No ambient audio — California is all-party consent. Mic the agent only, on camera, with consent.',
    'Sign in truthfully as a vendor (name + Roto Flight Media) — never pose as a buyer.',
  ],
};

// ── Time helpers (Pacific) ──────────────────────────────────────────────────
function fmtPtTime(iso) {
  if (!iso) return '';
  return new Date(iso)
    .toLocaleTimeString('en-US', { timeZone: PT, hour: 'numeric', minute: '2-digit' })
    .replace(':00', '');
}
function fmtWindow(startIso, endIso) {
  if (!startIso) return '—';
  const s = fmtPtTime(startIso);
  if (!endIso) return s;
  const e = fmtPtTime(endIso);
  const sameSuffix = s.slice(-2) === e.slice(-2);
  return `${sameSuffix ? s.slice(0, -3) : s}–${e}`;
}
function fmtPrice(p) {
  const n = parseFloat(p);
  if (!n || isNaN(n)) return '';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}
/** Minutes from now until an ISO instant (negative = past). */
function minsUntil(iso) {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / 60000);
}
function isVillage(b) { return (b || '').toLowerCase().includes('village'); }

/** Native turn-by-turn nav URL to a stop. */
function navUrl(oh) {
  const base = 'https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=';
  if (oh?.lat != null && oh?.lng != null) return base + `${oh.lat},${oh.lng}`;
  return base + encodeURIComponent([oh?.address, oh?.city, 'CA'].filter(Boolean).join(', '));
}

export default function RunMode({ planId, onSelectPlan, onExit }) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { toast } = useToast();

  const { data: plans = [] } = useOhPlans();
  const { data: openHouses = [] } = useOpenHouses();
  const updatePlan = useUpdateOhPlan();
  const updateStop = useUpdateOhPlanStop();
  const optimizeMut = useOptimizeRoute();

  // Default to the passed plan, else most recent active, else most recent plan
  const effectivePlanId = useMemo(() => {
    if (planId) return planId;
    const active = [...plans]
      .filter((p) => p.status === 'active')
      .sort((a, b) => String(b.planDate).localeCompare(String(a.planDate)))[0];
    if (active) return active.id;
    return [...plans].sort((a, b) => String(b.planDate).localeCompare(String(a.planDate)))[0]?.id || null;
  }, [planId, plans]);

  const plan = plans.find((p) => p.id === effectivePlanId) || null;
  const { data: stops = [] } = useOhPlanStops(effectivePlanId);

  const [pitchOpen, setPitchOpen] = useState(false);
  const [outcomeStop, setOutcomeStop] = useState(null);
  const [rerouting, setRerouting] = useState(false);
  const [rerouteFrom, setRerouteFrom] = useState(null);

  const ohById = useMemo(
    () => Object.fromEntries(openHouses.map((o) => [o.id, o])),
    [openHouses],
  );

  // Stops ordered by position, joined to their open house
  const rows = useMemo(
    () => [...stops]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((stop) => ({ stop, oh: ohById[stop.openHouseId] })),
    [stops, ohById],
  );

  const doneCount = rows.filter((r) => r.stop.status === 'done' || r.stop.status === 'skipped').length;
  const totalCount = rows.length;
  const currentRow = rows.find((r) => r.stop.status === 'pending' || r.stop.status === 'arrived') || null;
  const currentIndex = currentRow ? rows.indexOf(currentRow) : totalCount;
  const allComplete = totalCount > 0 && !currentRow;
  const dwellMin = plan?.data?.dwellMin ?? 20;

  // Schedule status vs the current stop's ETA (only meaningful on the day itself —
  // hidden when the plan's ETAs are hours away, e.g. previewing a future plan)
  const schedChip = useMemo(() => {
    if (!currentRow?.stop?.eta) return null;
    const delta = -minsUntil(currentRow.stop.eta); // +ve = we're past the ETA
    if (Math.abs(delta) > 180) return null;
    if (delta > 10) return { label: `Running ${delta} min behind`, color: 'error' };
    if (delta < -5) return { label: `${-delta} min ahead`, color: 'success' };
    return { label: 'On schedule', color: 'default' };
  }, [currentRow]);

  // ── Actions ───────────────────────────────────────────────────────────
  const markArrived = (stop) => {
    updateStop.mutate({ id: stop.id, status: 'arrived', visitedAt: new Date().toISOString() });
  };

  const maybeSuggestReroute = (justFinished) => {
    const remaining = rows.filter(
      (r) => r.stop.id !== justFinished.id && r.stop.status === 'pending',
    );
    if (remaining.length >= 1) setRerouteFrom(justFinished);
  };

  const saveOutcome = (stop, outcome) => {
    updateStop.mutate(
      { id: stop.id, status: 'done', visitedAt: stop.visitedAt || new Date().toISOString(), outcome },
      {
        onSuccess: () => {
          logAction({
            action: 'Log OH Visit',
            user: profile?.full_name || profile?.email,
            description: `Logged open-house visit — ${ohById[stop.openHouseId]?.address || ''}`,
            details: { planId: plan?.id, stopId: stop.id, ...outcome },
          });
          toast('Outcome logged', 'success');
        },
      },
    );
    setOutcomeStop(null);
    maybeSuggestReroute(stop);
  };

  const skipStop = (stop) => {
    updateStop.mutate(
      { id: stop.id, status: 'skipped', visitedAt: new Date().toISOString() },
      { onSuccess: () => toast('Stop skipped', 'info') },
    );
    maybeSuggestReroute(stop);
  };

  const doReroute = async () => {
    if (!rerouteFrom || !plan) return;
    const originOh = ohById[rerouteFrom.openHouseId];
    if (originOh?.lat == null || originOh?.lng == null) {
      toast('Current stop has no coordinates to route from', 'error');
      setRerouteFrom(null);
      return;
    }
    const remaining = rows.filter(
      (r) => r.stop.id !== rerouteFrom.id && r.stop.status === 'pending' && r.oh?.lat != null && r.oh?.startAt && r.oh?.endAt,
    );
    if (remaining.length < 1) { setRerouteFrom(null); return; }
    setRerouting(true);
    try {
      const stopPayload = remaining.map((r) => ({
        id: r.stop.id,
        lat: parseFloat(r.oh.lat),
        lng: parseFloat(r.oh.lng),
        windowStart: r.oh.startAt,
        windowEnd: r.oh.endAt,
      }));
      const res = await optimizeMut.mutateAsync({
        origin: { lat: parseFloat(originOh.lat), lng: parseFloat(originOh.lng) },
        departAt: new Date().toISOString(),
        dwellMin,
        stops: stopPayload,
      });
      const newRoute = {
        order: res.order || [],
        schedule: res.schedule || [],
        droppedStops: res.droppedStops || [],
        totalDriveMin: res.totalDriveMin ?? null,
        finishAt: res.finishAt || null,
        path: res.path || [],
        warnings: res.warnings || [],
        optimizedAt: new Date().toISOString(),
        rerouted: true,
      };
      await updatePlan.mutateAsync({ id: plan.id, route: newRoute });
      // Re-position pending stops after all completed ones; write new ETAs
      const schedById = Object.fromEntries(newRoute.schedule.map((s) => [s.id, s]));
      let pos = doneCount; // completed stops keep their leading positions
      for (const stopId of [...newRoute.order, ...newRoute.droppedStops]) {
        const sched = schedById[stopId];
        await updateStop.mutateAsync({
          id: stopId,
          position: pos++,
          eta: sched?.arriveAt || null,
          etd: sched?.departAt || null,
        });
      }
      const finish = newRoute.finishAt ? `done by ${fmtPtTime(newRoute.finishAt)}` : 'route updated';
      toast(
        newRoute.droppedStops.length
          ? `Re-routed — ${newRoute.droppedStops.length} won't fit, ${finish}`
          : `Re-routed — ${finish}`,
        newRoute.droppedStops.length ? 'info' : 'success',
      );
    } catch (e) {
      toast(`Re-route failed: ${e.message}`, 'error');
    } finally {
      setRerouting(false);
      setRerouteFrom(null);
    }
  };

  const finishDay = () => {
    if (plan && plan.status !== 'done') {
      updatePlan.mutate({ id: plan.id, status: 'done' });
    }
    navigate('/openhouse?tab=followup');
  };

  // ── Render ────────────────────────────────────────────────────────────
  if (!plan) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No plan selected. Build one in the Plan tab first.
        </Typography>
        {plans.length > 0 && (
          <FormControl sx={{ minWidth: 260 }} size="small">
            <InputLabel>Pick a plan</InputLabel>
            <Select label="Pick a plan" value="" onChange={(e) => onSelectPlan?.(e.target.value)}>
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.title || p.planDate} · {p.status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', pb: 6 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{plan.title || 'Run'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {allComplete ? `${totalCount} stops complete` : `Stop ${Math.min(currentIndex + 1, totalCount)} of ${totalCount}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {schedChip && <Chip size="small" label={schedChip.label} color={schedChip.color} />}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select value={plan.id} onChange={(e) => onSelectPlan?.(e.target.value)} variant="standard">
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.title || p.planDate}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Re-route suggestion */}
      {rerouteFrom && (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={doReroute} disabled={rerouting} startIcon={rerouting ? <CircularProgress size={14} /> : <RouteIcon />}>
              Re-route now
            </Button>
          }
        >
          Schedule changed — re-optimize the rest of the day from here?
        </Alert>
      )}

      {/* Current stop */}
      {currentRow && currentRow.oh && (
        <CurrentStopCard
          row={currentRow}
          pitchOpen={pitchOpen}
          setPitchOpen={setPitchOpen}
          onArrived={() => markArrived(currentRow.stop)}
          onLogOutcome={() => setOutcomeStop(currentRow.stop)}
          onSkip={() => skipStop(currentRow.stop)}
        />
      )}

      {/* All-done summary */}
      {allComplete && (
        <DaySummary rows={rows} onFollowUp={finishDay} onExit={onExit} />
      )}

      {/* Stop list */}
      <Typography variant="overline" color="text.secondary" sx={{ mt: 3, display: 'block' }}>Itinerary</Typography>
      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
        {rows.map((r, i) => {
          const done = r.stop.status === 'done';
          const skipped = r.stop.status === 'skipped';
          const isCurrent = r.stop.id === currentRow?.stop?.id;
          return (
            <Paper
              key={r.stop.id}
              variant="outlined"
              sx={{
                p: 1, display: 'flex', alignItems: 'center', gap: 1,
                borderColor: isCurrent ? 'primary.main' : 'divider',
                borderWidth: isCurrent ? 2 : 1,
                opacity: skipped ? 0.55 : 1,
              }}
            >
              <Box sx={{ width: 24, textAlign: 'center', flexShrink: 0 }}>
                {done ? <CheckCircleIcon color="success" fontSize="small" />
                  : skipped ? <SkipNextIcon color="disabled" fontSize="small" />
                  : <Typography variant="caption" sx={{ fontWeight: 700 }}>{i + 1}</Typography>}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: isCurrent ? 700 : 500, textDecoration: skipped ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.oh?.address || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmtWindow(r.oh?.startAt, r.oh?.endAt)}
                  {r.stop.eta ? ` · ETA ${fmtPtTime(r.stop.eta)}` : ''}
                </Typography>
              </Box>
              {isVillage(r.oh?.brokerage) && <Chip size="small" label="Village" sx={{ bgcolor: VILLAGE_COLOR, color: '#fff', height: 20 }} />}
            </Paper>
          );
        })}
      </Stack>

      {rows.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>This plan has no stops. Add some in the Browse tab.</Alert>
      )}

      <OutcomeDialog
        stop={outcomeStop}
        oh={outcomeStop ? ohById[outcomeStop.openHouseId] : null}
        onClose={() => setOutcomeStop(null)}
        onSave={saveOutcome}
      />
    </Box>
  );
}

// ── Current stop card ────────────────────────────────────────────────────
function CurrentStopCard({ row, pitchOpen, setPitchOpen, onArrived, onLogOutcome, onSkip }) {
  const { oh, stop } = row;
  const closesIn = minsUntil(oh.endAt);
  // Urgency chip only near the window close (hidden when hours away, e.g. future plan)
  const showClosing = closesIn != null && closesIn <= 90 && closesIn > -180;
  const arrived = stop.status === 'arrived';

  return (
    <Paper elevation={3} sx={{ p: 2, borderRadius: 3, borderTop: '4px solid', borderColor: 'primary.main' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{oh.address}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[oh.city, fmtPrice(oh.price), [oh.beds && `${oh.beds} bd`, oh.baths && `${oh.baths} ba`].filter(Boolean).join(' / ')].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<NavigationIcon />}
          href={navUrl(oh)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ flexShrink: 0 }}
        >
          Nav
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip icon={<PlaceIcon />} size="small" label={`Open ${fmtWindow(oh.startAt, oh.endAt)}`} />
        {showClosing && closesIn >= 0 && (
          <Chip size="small" color={closesIn < 30 ? 'error' : 'default'} label={`closes in ${closesIn} min`} />
        )}
        {showClosing && closesIn < 0 && <Chip size="small" color="error" label="window closed" />}
        {stop.eta && <Chip size="small" variant="outlined" label={`ETA ${fmtPtTime(stop.eta)}`} />}
        {isVillage(oh.brokerage) && <Chip size="small" label="Village Properties" sx={{ bgcolor: VILLAGE_COLOR, color: '#fff' }} />}
      </Stack>

      {(oh.agentName || oh.agentPhone) && (
        <Paper variant="outlined" sx={{ mt: 1.5, p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{oh.agentName || 'Listing agent'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {oh.brokerage || ''}{oh.contactId ? ' · In CRM' : ''}
            </Typography>
          </Box>
          {oh.agentPhone && (
            <Button size="small" variant="outlined" startIcon={<PhoneIcon />} href={`tel:${oh.agentPhone}`}>
              {oh.agentPhone}
            </Button>
          )}
        </Paper>
      )}

      {/* Pitch cheat sheet */}
      <Accordion expanded={pitchOpen} onChange={() => setPitchOpen((v) => !v)} sx={{ mt: 1.5, boxShadow: 'none', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Pitch cheat sheet</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PitchLine label="Open" text={PITCH.intro} />
          <PitchLine label="The offer" text={PITCH.offer} />
          <PitchLine label="Permission" text={PITCH.permission} />
          <PitchLine label="If hesitant" text={PITCH.fallback} />
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="error" sx={{ fontWeight: 700 }}>Red lines</Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0.5, '& li': { fontSize: '0.78rem', color: 'text.secondary', mb: 0.25 } }}>
            {PITCH.redLines.map((l, i) => <li key={i}>{l}</li>)}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Actions */}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        {!arrived ? (
          <Button fullWidth size="large" variant="contained" startIcon={<PlaceIcon />} onClick={onArrived}>
            Arrived
          </Button>
        ) : (
          <Button fullWidth size="large" variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={onLogOutcome}>
            Done — log outcome
          </Button>
        )}
        <Button size="large" variant="outlined" color="inherit" startIcon={<SkipNextIcon />} onClick={onSkip}>
          Skip
        </Button>
      </Stack>
    </Paper>
  );
}

function PitchLine({ label, text }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{text}</Typography>
    </Box>
  );
}

// ── Outcome dialog ───────────────────────────────────────────────────────
function OutcomeDialog({ stop, oh, onClose, onSave }) {
  const [talked, setTalked] = useState(true);
  const [shootType, setShootType] = useState('none');
  const [gotCell, setGotCell] = useState(false);
  const [agentCell, setAgentCell] = useState('');
  const [agentIg, setAgentIg] = useState('');
  const [notes, setNotes] = useState('');

  // Reset fields when a different stop opens
  useMemo(() => {
    if (stop) {
      setTalked(true); setShootType('none'); setGotCell(false);
      setAgentCell(oh?.agentPhone || ''); setAgentIg(''); setNotes('');
    }
  }, [stop?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stop) return null;

  const handleSave = () => {
    onSave(stop, {
      ...(stop.outcome || {}),
      talked,
      shootType,
      gotCell,
      agentCell: gotCell ? agentCell.trim() : (stop.outcome?.agentCell || ''),
      agentIg: agentIg.trim(),
      notes: notes.trim(),
    });
  };

  return (
    <Dialog open={!!stop} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 0.5 }}>
        Log visit
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{oh?.address}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <FormControlLabel control={<Switch checked={talked} onChange={(e) => setTalked(e.target.checked)} />} label="Talked to the agent" />

          <Box>
            <Typography variant="caption" color="text.secondary">Shot</Typography>
            <ToggleButtonGroup fullWidth exclusive size="small" value={shootType} onChange={(e, v) => v && setShootType(v)} sx={{ mt: 0.5 }}>
              <ToggleButton value="none">None</ToggleButton>
              <ToggleButton value="teaser">Teaser</ToggleButton>
              <ToggleButton value="agent_on_camera">Agent on cam</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <FormControlLabel control={<Switch checked={gotCell} onChange={(e) => setGotCell(e.target.checked)} />} label="Got their cell" />
          {gotCell && (
            <Stack spacing={1}>
              <TextField size="small" label="Agent cell" value={agentCell} onChange={(e) => setAgentCell(e.target.value)} />
              <TextField size="small" label="Instagram handle" value={agentIg} onChange={(e) => setAgentIg(e.target.value)} placeholder="@handle" />
            </Stack>
          )}

          <TextField size="small" label="Notes" multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Day summary ──────────────────────────────────────────────────────────
function DaySummary({ rows, onFollowUp, onExit }) {
  const visited = rows.filter((r) => r.stop.status === 'done').length;
  const convos = rows.filter((r) => r.stop.outcome?.talked).length;
  const shoots = rows.filter((r) => r.stop.outcome?.shootType && r.stop.outcome.shootType !== 'none').length;
  const cells = rows.filter((r) => r.stop.outcome?.gotCell).length;

  return (
    <Paper elevation={2} sx={{ p: 2.5, borderRadius: 3, mt: 1, textAlign: 'center' }}>
      <FlagIcon color="success" sx={{ fontSize: 36 }} />
      <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>Day complete</Typography>
      <Stack direction="row" spacing={2} justifyContent="center" sx={{ my: 2, flexWrap: 'wrap' }}>
        <Stat n={visited} label="Visited" />
        <Stat n={convos} label="Conversations" />
        <Stat n={shoots} label="Shoots" />
        <Stat n={cells} label="Cells" />
      </Stack>
      <Stack direction="row" spacing={1} justifyContent="center">
        <Button variant="contained" onClick={onFollowUp}>Go to Follow-up</Button>
        <Button variant="outlined" color="inherit" onClick={onExit}>Back to plan</Button>
      </Stack>
    </Paper>
  );
}

function Stat({ n, label }) {
  return (
    <Box sx={{ minWidth: 64 }}>
      <Typography variant="h5" sx={{ fontWeight: 800 }}>{n}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}
